// Per-Location Time Zones — Phase 6 (TZ ambiguity suffix)
//
// Display rule:
//   - Single-location view + browser TZ === location TZ → no suffix
//   - Single-location view + browser TZ ≠ location TZ → append browser-TZ abbrev
//   - Multi-location / All-Locations / unknown loc → append browser-TZ abbrev
//
// The suffix is the BROWSER's TZ abbreviation (ET/CT/MT/PT). It tells the
// user "the time you're seeing is in YOUR clock, not the data's location" —
// which is the rule the user confirmed: "viewer in NJ viewing LA loc at 2 PM
// PT sees 5 PM ET". When the suffix is shown, the times are in viewer time.

const IANA_TO_ABBREV = {
    'America/New_York':    'ET',
    'America/Chicago':     'CT',
    'America/Denver':      'MT',
    'America/Los_Angeles': 'PT',
    'America/Phoenix':     'MST',  // no DST — not in our allow-list but defensive
    'America/Anchorage':   'AKT',
    'Pacific/Honolulu':    'HT',
};

let _cachedBrowserTz = null;
let _cachedBrowserAbbrev = null;

/** Returns the browser's IANA timezone string (e.g. 'America/Los_Angeles'). */
export function browserTimezone() {
    if (_cachedBrowserTz) return _cachedBrowserTz;
    try {
        _cachedBrowserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    } catch {
        _cachedBrowserTz = 'America/New_York';
    }
    return _cachedBrowserTz;
}

/** Returns a short abbrev (ET/CT/MT/PT/...) for the browser's TZ. Falls back
 * to the platform short name (e.g. "GMT-7") for non-US zones.
 */
export function browserTzAbbrev() {
    if (_cachedBrowserAbbrev) return _cachedBrowserAbbrev;
    const tz = browserTimezone();
    if (IANA_TO_ABBREV[tz]) {
        _cachedBrowserAbbrev = IANA_TO_ABBREV[tz];
        return _cachedBrowserAbbrev;
    }
    // Fallback: use Intl to get a short timeZoneName, e.g. "EDT" / "PDT"
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
            .formatToParts(new Date());
        const tzName = parts.find(p => p.type === 'timeZoneName');
        if (tzName?.value) {
            // "EDT" → "ET", "PST" → "PT", etc. Simple last-char-strip when ends in T.
            const v = tzName.value;
            if (v.length === 3 && v.endsWith('T')) {
                _cachedBrowserAbbrev = v[0] + 'T';
                return _cachedBrowserAbbrev;
            }
            _cachedBrowserAbbrev = v;
            return _cachedBrowserAbbrev;
        }
    } catch {}
    _cachedBrowserAbbrev = 'ET';
    return _cachedBrowserAbbrev;
}

/** Returns the appropriate TZ marker suffix to append to a rendered timestamp.
 *
 * @param {string|null} activeLocTz - The currently-viewed location's IANA TZ,
 *   or null/undefined for multi-loc / All-Locations / unknown.
 * @returns {string} - Either '' (no marker needed) or a leading-space-prefixed
 *   abbrev (e.g. ' ET') ready to be concatenated onto a formatted timestamp.
 */
export function tzMarker(activeLocTz) {
    const browserTz = browserTimezone();
    // Single-loc + same TZ as browser → no marker.
    if (activeLocTz && activeLocTz === browserTz) return '';
    // Otherwise → marker (browser TZ abbrev).
    return ' ' + browserTzAbbrev();
}
